// client/src/pages/IdPasswordFind.js
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import EmailVerification from '../components/EmailVerification';
import PhoneVerification from '../components/PhoneVerification';
import '../styles/pages/PublicPages.css';

const API_URL = process.env.REACT_APP_API_URL;

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function Modal({ text, onClose }) {
  return (
    <div className="modal-background">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={{ float: 'right', padding: '8px 16px', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer' }}>닫기</button>
        <div style={{ marginTop: 24 }}>{text}</div>
      </div>
    </div>
  );
}

function IdPasswordFind() {
  const q = useQuery();
  const tab = q.get('tab') || 'id';

  const [active, setActive] = useState(tab);
  useEffect(() => { setActive(tab); }, [tab]);

  // 공통
  const [modal, setModal] = useState('');
  const [foundIds, setFoundIds] = useState([]);

  // ID 찾기(이메일)
  const [idMethod, setIdMethod] = useState('email');
  const [formEmail, setFormEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [canVerifyEmail, setCanVerifyEmail] = useState(false);
  const emailVerRef = useRef(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // ID 찾기(휴대폰)
  const [formPhone, setFormPhone] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [canVerifyPhone, setCanVerifyPhone] = useState(false);
  const phoneVerRef = useRef(null);
  const [checkingPhone, setCheckingPhone] = useState(false);

  // 비밀번호 재발급
  const [pwUserId, setPwUserId] = useState('');
  const [pwUserExists, setPwUserExists] = useState(false);
  const [pwEmail, setPwEmail] = useState('');
  const [pwEmailVerified, setPwEmailVerified] = useState(false);
  const [pwCanVerify, setPwCanVerify] = useState(false);
  const pwEmailRef = useRef(null);
  const [pwCheckingId, setPwCheckingId] = useState(false);
  const [pwCheckingEmail, setPwCheckingEmail] = useState(false);
  const [pwSubmitting, setPwSubmitting] = useState(false);

  useEffect(() => {
    if (active === 'id') {
      setIdMethod('email');
      setFormEmail(''); setFormPhone('');
      setEmailVerified(false); setPhoneVerified(false);
      setCanVerifyEmail(false); setCanVerifyPhone(false);
      setCheckingEmail(false); setCheckingPhone(false);
      setFoundIds([]);
    } else if (active === 'pw') {
      setPwUserId(''); setPwUserExists(false);
      setPwEmail(''); setPwEmailVerified(false);
      setPwCanVerify(false);
      setPwCheckingId(false); setPwCheckingEmail(false);
      setPwSubmitting(false);
    }
  }, [active]);

  useEffect(() => {
    if (pwCanVerify) {
      // 컴포넌트가 렌더되어 ref가 연결된 다음에 모달 열기+코드 전송
      setTimeout(() => pwEmailRef.current?.openAndRequest(), 0);
    }
  }, [pwCanVerify]);

  // ===== ID 찾기: 이메일 =====
  const startEmailCheck = async () => {
    setEmailVerified(false); setFoundIds([]); setCanVerifyEmail(false);
    if (!formEmail.trim()) { setModal('이메일을 입력하세요.'); return; }
    setCheckingEmail(true);
    try {
      const res = await fetch(`${API_URL}/api/account/recovery/check-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formEmail.trim() })
      });
      const data = await res.json();
      if (res.ok && data.ok && data.exists) setCanVerifyEmail(true);
      else setModal('해당 이메일로 가입된 아이디는 존재하지 않습니다.');
    } catch { setModal('서버 요청 실패'); }
    setCheckingEmail(false);
  };
  useEffect(() => { if (canVerifyEmail) setTimeout(() => emailVerRef.current?.openAndRequest(), 0); }, [canVerifyEmail]);

  // ===== ID 찾기: 휴대폰 =====
  const startPhoneCheck = async () => {
    const phone = formPhone.replace(/[^\d]/g, '').slice(0, 11);
    setPhoneVerified(false); setFoundIds([]); setCanVerifyPhone(false);
    if (!/^\d{10,11}$/.test(phone)) { setModal('올바른 휴대폰 번호를 입력하세요.'); return; }
    setCheckingPhone(true);
    try {
      const res = await fetch(`${API_URL}/api/account/recovery/check-phone`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone })
      });
      const data = await res.json();
      if (res.ok && data.ok && data.exists) setCanVerifyPhone(true);
      else setModal('해당 번호로 가입된 아이디는 존재하지 않습니다.');
    } catch { setModal('서버 요청 실패'); }
    setCheckingPhone(false);
  };
  useEffect(() => { if (canVerifyPhone) setTimeout(() => phoneVerRef.current?.openAndRequest(), 0); }, [canVerifyPhone]);

  // ID 조회(이메일/휴대폰 공통)
  useEffect(() => {
    const run = async () => {
      try {
        let url = '', body = {};
        if (emailVerified) { url = `${API_URL}/api/account/recovery/id-by-email`; body = { email: formEmail.trim() }; }
        else if (phoneVerified) { url = `${API_URL}/api/account/recovery/id-by-phone`; body = { phone: formPhone.replace(/[^\d]/g, '').slice(0, 11) }; }
        else return;

        const res = await fetch(url, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        const text = await res.text();
        let data = {}; try { data = JSON.parse(text); } catch {}
        if (res.ok && data.ok) {
          setFoundIds(Array.isArray(data.userIds) ? data.userIds : (data.userId ? [data.userId] : []));
        } else {
          setFoundIds([]); setModal((data && data.message) || text || '아이디 조회 실패');
        }
      } catch { setFoundIds([]); setModal('아이디 조회 요청 실패'); }
    };
    run();
  }, [emailVerified, phoneVerified, formEmail, formPhone]);

  // ===== 비밀번호 재발급: ID 확인 → 이메일 매칭 확인 → 이메일 인증 → 재발급 =====
  const checkPwUserId = async () => {
    setPwUserExists(false); setPwEmail(''); setPwEmailVerified(false); setPwCanVerify(false);
    if (!pwUserId.trim()) { setModal('ID를 입력하세요.'); return; }
    setPwCheckingId(true);
    try {
      const res = await fetch(`${API_URL}/api/account/recovery/check-user`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: pwUserId.trim() })
      });
      const data = await res.json();
      if (res.ok && data.ok && data.exists) setPwUserExists(true);
      else setModal('해당 ID는 존재하지 않습니다.');
    } catch { setModal('서버 요청 실패'); }
    setPwCheckingId(false);
  };

  const checkPwUserEmail = async () => {
    setPwEmailVerified(false); setPwCanVerify(false);
    if (!pwUserId.trim()) { setModal('ID를 먼저 확인하세요.'); return; }
    if (!pwEmail.trim()) { setModal('이메일을 입력하세요.'); return; }
    setPwCheckingEmail(true);
    try {
      const res = await fetch(`${API_URL}/api/account/recovery/check-user-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: pwUserId.trim(), email: pwEmail.trim() })
      });
      const data = await res.json();
      if (res.ok && data.ok && data.match) {
        // 여기서는 모달을 바로 열지 않고, 렌더 완료 후 useEffect에서 열도록 플래그만 켭니다.
        setPwCanVerify(true);
      } else {
        setModal('해당 ID에 등록된 이메일이 아닙니다.');
      }
    } catch { setModal('서버 요청 실패'); }
    setPwCheckingEmail(false);
  };

  const submitPasswordReset = async () => {
    if (!pwUserId.trim() || !pwEmailVerified) return;
    setPwSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/account/recovery/password-reset`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: pwUserId.trim(), email: pwEmail.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setModal('임시 비밀번호가 이메일로 전송되었습니다.');
      } else {
        setModal(data.message || '임시 비밀번호 발급 실패');
      }
    } catch {
      setModal('임시 비밀번호 발급 요청 실패');
    }
    setPwSubmitting(false);
  };

  return (
    <div className="id-password-find">
      <Header />

      <h1 className="id-password-find__title">아이디/비밀번호 찾기</h1>

      <div className="id-password-find__tabs">
        <button 
          type="button" 
          onClick={() => setActive('id')}
          className={`id-password-find__tab ${active === 'id' ? 'id-password-find__tab--active' : 'id-password-find__tab--inactive'}`}
        >ID 찾기</button>
        <button 
          type="button" 
          onClick={() => setActive('pw')}
          className={`id-password-find__tab ${active === 'pw' ? 'id-password-find__tab--active' : 'id-password-find__tab--inactive'}`}
        >비밀번호 재발급</button>
      </div>

      <div className="id-password-find__content">
        {active === 'id' && (
          <div>
            <div className="id-password-find__methods">
              <label className="id-password-find__method-label">
                <input type="checkbox" checked={idMethod === 'email'} onChange={() => setIdMethod('email')} />
                <span>등록한 이메일로 ID찾기</span>
              </label>
              <label className="id-password-find__method-label">
                <input type="checkbox" checked={idMethod === 'phone'} onChange={() => setIdMethod('phone')} />
                <span>등록한 핸드폰 번호로 ID찾기</span>
              </label>
            </div>

            {idMethod === 'email' && (
              <div className="id-password-find__section">
                <div className="id-password-find__section-title">등록한 이메일로 ID 찾기</div>
                <div className="id-password-find__input-row">
                  <input
                    className="id-password-find__input"
                    placeholder="이메일 주소 입력"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    disabled={emailVerified}
                  />
                  <button type="button" onClick={startEmailCheck}
                    disabled={checkingEmail || emailVerified}
                    className={`id-password-find__button ${emailVerified ? 'id-password-find__button--verified' : ''}`}>
                    {emailVerified ? '인증완료' : (checkingEmail ? '확인 중...' : '인증 시작')}
                  </button>
                </div>
                {canVerifyEmail && (
                  <div className="id-password-find__verification">
                    <EmailVerification
                      ref={emailVerRef}
                      formData={{ email: formEmail }}
                      setFormData={({ email }) => setFormEmail(email)}
                      emailVerified={emailVerified}
                      setEmailVerified={setEmailVerified}
                      onVerified={() => setEmailVerified(true)}
                      inline={false}
                    />
                  </div>
                )}
                {(emailVerified || phoneVerified) && foundIds.length > 0 && (
                  <div className="id-password-find__result">
                    가입하신 ID는 {foundIds.map((id, i) => <strong key={id}>{i ? `, ${id}` : id}</strong>)} 입니다.
                  </div>
                )}
              </div>
            )}

            {idMethod === 'phone' && (
              <div className="id-password-find__section">
                <div className="id-password-find__section-title">등록한 핸드폰 번호로 ID 찾기</div>
                <div className="id-password-find__input-row">
                  <input
                    className="id-password-find__input"
                    placeholder="휴대폰 번호 입력(숫자만)"
                    value={formPhone}
                    onChange={(e) => setFormPhone(e.target.value.replace(/[^\d]/g, '').slice(0, 11))}
                    disabled={phoneVerified}
                  />
                  <button type="button" onClick={startPhoneCheck}
                    disabled={checkingPhone || phoneVerified}
                    className={`id-password-find__button ${phoneVerified ? 'id-password-find__button--verified' : ''}`}>
                    {phoneVerified ? '인증완료' : (checkingPhone ? '확인 중...' : '인증 시작')}
                  </button>
                </div>
                {canVerifyPhone && (
                  <div className="id-password-find__verification">
                    <PhoneVerification
                      ref={phoneVerRef}
                      formData={{ phone: formPhone }}
                      setFormData={({ phone }) => setFormPhone(phone)}
                      phoneVerified={phoneVerified}
                      setPhoneVerified={setPhoneVerified}
                      onVerified={() => setPhoneVerified(true)}
                      inline={false}
                    />
                  </div>
                )}
                {(emailVerified || phoneVerified) && foundIds.length > 0 && (
                  <div className="id-password-find__result">
                    가입하신 ID는 {foundIds.map((id, i) => <strong key={id}>{i ? `, ${id}` : id}</strong>)} 입니다.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {active === 'pw' && (
          <div>
            <div className="id-password-find__section-title">등록한 이메일로 비밀번호 재발급</div>

            {/* 1) ID 확인 */}
            <div className="id-password-find__input-row">
              <input
                className="id-password-find__input"
                placeholder="ID 입력"
                value={pwUserId}
                onChange={(e) => setPwUserId(e.target.value)}
                disabled={pwUserExists}
              />
              <button type="button" onClick={checkPwUserId}
                disabled={pwCheckingId || pwUserExists}
                className={`id-password-find__button ${pwUserExists ? 'id-password-find__button--verified' : ''}`}>
                {pwUserExists ? '확인 완료' : (pwCheckingId ? '확인 중...' : 'ID 확인')}
              </button>
            </div>

            {/* 2) 이메일 매칭 확인 + 인증 */}
            {pwUserExists && (
              <div className="id-password-find__section">
                <div className="id-password-find__input-row">
                  <input
                    className="id-password-find__input"
                    placeholder="해당 ID에 등록된 이메일 입력"
                    value={pwEmail}
                    onChange={(e) => setPwEmail(e.target.value)}
                    disabled={pwEmailVerified}
                  />
                  <button type="button" onClick={checkPwUserEmail}
                    disabled={pwCheckingEmail || pwEmailVerified}
                    className={`id-password-find__button ${pwEmailVerified ? 'id-password-find__button--verified' : ''}`}>
                    {pwEmailVerified ? '인증완료' : (pwCheckingEmail ? '확인 중...' : '인증 시작')}
                  </button>
                </div>

                {pwCanVerify && (
                  <div className="id-password-find__verification">
                    <EmailVerification
                      ref={pwEmailRef}
                      formData={{ email: pwEmail }}
                      setFormData={({ email }) => setPwEmail(email)}
                      emailVerified={pwEmailVerified}
                      setEmailVerified={setPwEmailVerified}
                      onVerified={() => setPwEmailVerified(true)}
                      inline={false}
                    />
                  </div>
                )}
              </div>
            )}

            {/* 3) 임시 비밀번호 발급 */}
            {pwUserExists && pwEmailVerified && (
              <div className="id-password-find__section">
                <button type="button" onClick={submitPasswordReset}
                  disabled={pwSubmitting}
                  className="id-password-find__submit">
                  {pwSubmitting ? '발급 중...' : '임시 비밀번호 발급'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {modal && <Modal text={modal} onClose={() => setModal('')} />}
    </div>
  );
}

export default IdPasswordFind;